/**
 * Session Analytics - Comprehensive session quality analysis and recommendations
 *
 * Analyzes imaging sessions to provide:
 * - Overall session quality scores
 * - Optimal imaging window recommendations
 * - Statistical analysis of observing conditions
 * - Object distribution and diversity metrics
 */

import { objectTypeLabel } from './displayNames.js';

export class SessionAnalytics {
    constructor() {
        this.weights = {
            objectCount: 0.20,          // Number and quality of available objects
            moonInterference: 0.25,     // Moon interference level
            distribution: 0.20,         // Temporal distribution across the night
            altitude: 0.20,             // Altitude profile quality
            diversity: 0.15             // Object type diversity
        };
    }

    /**
     * Calculate comprehensive session quality score
     * @param {Array} objects - Ranked objects with visibility data
     * @param {Object} sessionParams - Session parameters (location, date, etc.)
     * @returns {Object} Session quality score and breakdown
     */
    calculateSessionQualityScore(objects, sessionParams) {
        if (!objects || objects.length === 0) {
            return {
                total: 0,
                grade: 'F',
                breakdown: {
                    objectCount: 0,
                    moonInterference: 0,
                    distribution: 0,
                    altitude: 0,
                    diversity: 0
                },
                analysis: {
                    strengths: [],
                    weaknesses: ['No objects found in search area'],
                    recommendations: ['Try expanding search radius or adjusting time window']
                }
            };
        }

        const scores = {
            objectCount: this.scoreObjectCount(objects),
            moonInterference: this.scoreMoonInterference(objects, sessionParams),
            distribution: this.scoreTemporalDistribution(objects),
            altitude: this.scoreAltitudeProfile(objects),
            diversity: this.scoreObjectDiversity(objects)
        };

        // Calculate weighted total
        const total = Object.keys(this.weights).reduce((sum, key) => {
            return sum + (scores[key] * this.weights[key]);
        }, 0);

        // Determine grade
        const grade = this.getGrade(total);

        // Generate analysis
        const analysis = this.generateAnalysis(scores, objects, sessionParams);

        return {
            total: Math.round(total * 10) / 10,
            grade: grade,
            breakdown: {
                objectCount: Math.round(scores.objectCount * 10) / 10,
                moonInterference: Math.round(scores.moonInterference * 10) / 10,
                distribution: Math.round(scores.distribution * 10) / 10,
                altitude: Math.round(scores.altitude * 10) / 10,
                diversity: Math.round(scores.diversity * 10) / 10
            },
            weights: this.weights,
            analysis: analysis
        };
    }

    /**
     * Score based on number and quality of available objects
     * @param {Array} objects - Objects with scores
     * @returns {number} Score 0-100
     */
    scoreObjectCount(objects) {
        const totalObjects = objects.length;
        const excellentObjects = objects.filter(o => o.score.total >= 80).length;
        const goodObjects = objects.filter(o => o.score.total >= 65 && o.score.total < 80).length;

        // Weight excellent objects more heavily
        const qualityWeightedCount = excellentObjects * 3 + goodObjects * 1.5 + (totalObjects - excellentObjects - goodObjects) * 0.5;

        // Score based on weighted count (diminishing returns after 10 objects)
        let score = Math.min(100, (qualityWeightedCount / 15) * 100);

        return score;
    }

    /**
     * Score based on moon interference across all objects
     * @param {Array} objects - Objects with visibility data
     * @param {Object} sessionParams - Session parameters
     * @returns {number} Score 0-100
     */
    scoreMoonInterference(objects, sessionParams) {
        if (objects.length === 0) return 0;

        // Calculate average moon-free percentage
        const avgMoonFreeRatio = objects.reduce((sum, obj) => {
            const totalDark = obj.visibility.darkTimeDuration || 0;
            const moonFree = obj.visibility.moonFreeDuration || 0;
            const ratio = totalDark > 0 ? moonFree / totalDark : 0;
            return sum + ratio;
        }, 0) / objects.length;

        // Score based on moon-free ratio
        return avgMoonFreeRatio * 100;
    }

    /**
     * Score based on temporal distribution of objects
     * @param {Array} objects - Objects with visibility data
     * @returns {number} Score 0-100
     */
    scoreTemporalDistribution(objects) {
        if (objects.length === 0) return 0;

        // Divide night into 4 quarters (evening, early night, late night, pre-dawn)
        const quarters = [0, 0, 0, 0];

        objects.forEach(obj => {
            const periods = obj.visibility.visiblePeriods || [];
            if (periods.length === 0) return;

            // Determine which quarters have objects
            const firstTime = periods[0].time.getHours();
            const lastTime = periods[periods.length - 1].time.getHours();

            // Evening (18-21)
            if (firstTime >= 18 && firstTime <= 21) quarters[0]++;
            // Early night (21-24)
            if ((firstTime >= 21 && firstTime <= 23) || lastTime >= 21) quarters[1]++;
            // Late night (0-3)
            if ((firstTime <= 3 && firstTime >= 0) || (lastTime <= 3 && lastTime >= 0)) quarters[2]++;
            // Pre-dawn (3-6)
            if (lastTime >= 3 && lastTime <= 6) quarters[3]++;
        });

        // Score based on coverage of night quarters
        const coveredQuarters = quarters.filter(q => q > 0).length;
        const evenness = 1 - (Math.max(...quarters) - Math.min(...quarters.filter(q => q > 0))) / Math.max(...quarters);

        // 50% for coverage, 50% for evenness
        return (coveredQuarters / 4) * 50 + evenness * 50;
    }

    /**
     * Score based on altitude profile of objects
     * @param {Array} objects - Objects with visibility data
     * @returns {number} Score 0-100
     */
    scoreAltitudeProfile(objects) {
        if (objects.length === 0) return 0;

        // Calculate average altitude metrics across all objects
        const avgAltitude = objects.reduce((sum, obj) =>
            sum + (obj.visibility.averageAltitude || 0), 0) / objects.length;

        const avgPeakAltitude = objects.reduce((sum, obj) =>
            sum + (obj.visibility.peakAltitude || 0), 0) / objects.length;

        // Score based on altitude (exponential curve favoring higher altitudes)
        const avgScore = Math.pow(avgAltitude / 90, 0.6) * 100;
        const peakScore = Math.pow(avgPeakAltitude / 90, 0.6) * 100;

        // 60% average, 40% peak
        return avgScore * 0.6 + peakScore * 0.4;
    }

    /**
     * Score based on diversity of object types
     * @param {Array} objects - Objects with type information
     * @returns {number} Score 0-100
     */
    scoreObjectDiversity(objects) {
        if (objects.length === 0) return 0;

        // Count unique object types
        const typeSet = new Set();
        const typeCounts = {};

        objects.forEach(obj => {
            const type = (objectTypeLabel(obj) || 'unknown').toLowerCase();
            typeSet.add(type);
            typeCounts[type] = (typeCounts[type] || 0) + 1;
        });

        const uniqueTypes = typeSet.size;

        // Calculate Shannon diversity index
        let diversity = 0;
        Object.values(typeCounts).forEach(count => {
            const proportion = count / objects.length;
            diversity -= proportion * Math.log(proportion);
        });

        // Normalize diversity (max diversity for 10 types is ~2.3)
        const normalizedDiversity = Math.min(1, diversity / 2.3);

        // Score: 40% for number of types, 60% for evenness of distribution
        const typeScore = Math.min(100, (uniqueTypes / 5) * 100); // 5+ types = max score
        const diversityScore = normalizedDiversity * 100;

        return typeScore * 0.4 + diversityScore * 0.6;
    }

    /**
     * Find optimal imaging windows within the session
     * @param {Array} objects - Objects with visibility data
     * @param {Object} sessionParams - Session parameters
     * @returns {Object} Optimal windows and recommendations
     */
    findOptimalImagingWindows(objects, sessionParams) {
        if (!objects || objects.length === 0) {
            return {
                bestWindows: [],
                recommendation: 'No objects available for imaging',
                analysis: 'Try adjusting search parameters or target direction'
            };
        }

        // Analyze night in 30-minute intervals
        const intervals = this.analyzeTimeIntervals(objects, sessionParams);

        // Find top 3 optimal windows
        const sortedIntervals = intervals
            .sort((a, b) => b.score - a.score)
            .slice(0, 3);

        const bestWindows = sortedIntervals.map(interval => ({
            start: this.formatTime(interval.startTime),
            end: this.formatTime(interval.endTime),
            score: Math.round(interval.score * 10) / 10,
            objectCount: interval.objects.length,
            topObjects: interval.objects.slice(0, 5).map(o => o.name || o.designation),
            avgAltitude: Math.round(interval.avgAltitude),
            moonFree: interval.moonFree,
            reasoning: interval.reasoning
        }));

        // Generate recommendation
        let recommendation = '';
        if (bestWindows.length > 0) {
            const best = bestWindows[0];
            recommendation = `Best imaging window: ${best.start} - ${best.end} (Score: ${best.score}/100). `;
            recommendation += `${best.objectCount} objects available with average altitude ${best.avgAltitude}°.`;
        }

        return {
            bestWindows: bestWindows,
            recommendation: recommendation,
            analysis: this.generateWindowAnalysis(bestWindows, objects)
        };
    }

    /**
     * Analyze night in time intervals
     * @param {Array} objects - Objects with visibility data
     * @param {Object} sessionParams - Session parameters
     * @returns {Array} Analyzed intervals
     */
    analyzeTimeIntervals(objects, sessionParams) {
        const intervals = [];
        const intervalMinutes = 30;

        // Create time range (18:00 to 06:00 next day)
        const startTime = new Date(sessionParams.observationDate + 'T18:00:00');
        const endTime = new Date(sessionParams.observationDate + 'T18:00:00');
        endTime.setDate(endTime.getDate() + 1);
        endTime.setHours(6, 0, 0, 0);

        for (let time = new Date(startTime); time < endTime; time.setMinutes(time.getMinutes() + intervalMinutes)) {
            const intervalEnd = new Date(time);
            intervalEnd.setMinutes(intervalEnd.getMinutes() + intervalMinutes);

            const intervalData = this.analyzeInterval(objects, time, intervalEnd);
            intervals.push({
                startTime: new Date(time),
                endTime: intervalEnd,
                ...intervalData
            });
        }

        return intervals;
    }

    /**
     * Analyze a specific time interval
     * @param {Array} objects - Objects with visibility data
     * @param {Date} startTime - Interval start
     * @param {Date} endTime - Interval end
     * @returns {Object} Interval analysis
     */
    analyzeInterval(objects, startTime, endTime) {
        const visibleObjects = [];
        let totalAltitude = 0;
        let moonFreeCount = 0;

        objects.forEach(obj => {
            const periods = obj.visibility.visiblePeriods || [];

            // Check if object is visible during this interval
            const visibleInInterval = periods.some(period => {
                const periodTime = new Date(period.time);
                return periodTime >= startTime && periodTime < endTime &&
                       period.isDark && period.altitude >= 30;
            });

            if (visibleInInterval) {
                visibleObjects.push(obj);

                // Calculate average altitude during interval
                const intervalPeriods = periods.filter(period => {
                    const periodTime = new Date(period.time);
                    return periodTime >= startTime && periodTime < endTime;
                });

                if (intervalPeriods.length > 0) {
                    const avgAlt = intervalPeriods.reduce((sum, p) => sum + p.altitude, 0) / intervalPeriods.length;
                    totalAltitude += avgAlt;

                    // Check if moon-free
                    if (intervalPeriods.every(p => p.isMoonFree)) {
                        moonFreeCount++;
                    }
                }
            }
        });

        const avgAltitude = visibleObjects.length > 0 ? totalAltitude / visibleObjects.length : 0;
        const moonFree = moonFreeCount === visibleObjects.length && visibleObjects.length > 0;

        // Score interval
        let score = 0;
        score += visibleObjects.length * 10; // Object count (up to 50 points for 5+ objects)
        score += Math.pow(avgAltitude / 90, 0.6) * 30; // Altitude (up to 30 points)
        score += moonFree ? 20 : 0; // Moon-free bonus (20 points)

        // Generate reasoning
        let reasoning = `${visibleObjects.length} objects visible`;
        if (avgAltitude > 60) reasoning += ', excellent altitude';
        else if (avgAltitude > 45) reasoning += ', good altitude';
        if (moonFree) reasoning += ', moon-free';

        return {
            objects: visibleObjects,
            avgAltitude: avgAltitude,
            moonFree: moonFree,
            score: Math.min(100, score),
            reasoning: reasoning
        };
    }

    /**
     * Generate comprehensive session statistics
     * @param {Array} objects - Objects with visibility data
     * @param {Object} sessionParams - Session parameters
     * @returns {Object} Statistical analysis
     */
    generateSessionStatistics(objects, sessionParams) {
        if (!objects || objects.length === 0) {
            return {
                totalObjects: 0,
                excellentQuality: 0,
                goodQuality: 0,
                fairQuality: 0,
                avgVisibilityHours: 0,
                avgAltitude: 0,
                moonInterferenceMinutes: 0,
                objectTypes: {},
                bestObject: null,
                longestVisible: null
            };
        }

        // Quality tier counts
        const excellentQuality = objects.filter(o => o.score.total >= 80).length;
        const goodQuality = objects.filter(o => o.score.total >= 65 && o.score.total < 80).length;
        const fairQuality = objects.filter(o => o.score.total >= 50 && o.score.total < 65).length;

        // Average metrics
        const avgVisibilityHours = objects.reduce((sum, o) =>
            sum + (o.visibility.moonFreeDuration || 0), 0) / objects.length;

        const avgAltitude = objects.reduce((sum, o) =>
            sum + (o.visibility.averageAltitude || 0), 0) / objects.length;

        // Moon interference calculation
        const totalDarkTime = objects.reduce((sum, o) =>
            sum + (o.visibility.darkTimeDuration || 0), 0);
        const totalMoonFreeTime = objects.reduce((sum, o) =>
            sum + (o.visibility.moonFreeDuration || 0), 0);
        const moonInterferenceMinutes = (totalDarkTime - totalMoonFreeTime) * 60 / objects.length;

        // Object types distribution
        const objectTypes = {};
        objects.forEach(obj => {
            const type = objectTypeLabel(obj) || 'Unknown';
            objectTypes[type] = (objectTypes[type] || 0) + 1;
        });

        // Best and longest visible objects
        const bestObject = objects[0]; // Already sorted by score
        const longestVisible = objects.reduce((max, obj) =>
            (obj.visibility.moonFreeDuration > (max?.visibility?.moonFreeDuration || 0)) ? obj : max
        , objects[0]);

        return {
            totalObjects: objects.length,
            excellentQuality: excellentQuality,
            goodQuality: goodQuality,
            fairQuality: fairQuality,
            avgVisibilityHours: Math.round(avgVisibilityHours * 10) / 10,
            avgAltitude: Math.round(avgAltitude * 10) / 10,
            moonInterferenceMinutes: Math.round(moonInterferenceMinutes),
            objectTypes: objectTypes,
            bestObject: {
                name: bestObject.name || bestObject.designation,
                score: bestObject.score.total,
                duration: bestObject.visibility.moonFreeDuration
            },
            longestVisible: {
                name: longestVisible.name || longestVisible.designation,
                duration: longestVisible.visibility.moonFreeDuration,
                score: longestVisible.score.total
            }
        };
    }

    /**
     * Get letter grade from numeric score
     * @param {number} score - Numeric score 0-100
     * @returns {string} Letter grade
     */
    getGrade(score) {
        if (score >= 90) return 'A+';
        if (score >= 85) return 'A';
        if (score >= 80) return 'A-';
        if (score >= 75) return 'B+';
        if (score >= 70) return 'B';
        if (score >= 65) return 'B-';
        if (score >= 60) return 'C+';
        if (score >= 55) return 'C';
        if (score >= 50) return 'C-';
        if (score >= 45) return 'D+';
        if (score >= 40) return 'D';
        return 'F';
    }

    /**
     * Generate qualitative analysis from scores
     * @param {Object} scores - Component scores
     * @param {Array} objects - Objects being analyzed
     * @param {Object} sessionParams - Session parameters
     * @returns {Object} Analysis with strengths, weaknesses, recommendations
     */
    generateAnalysis(scores, objects, sessionParams) {
        const strengths = [];
        const weaknesses = [];
        const recommendations = [];

        // Object count analysis
        if (scores.objectCount >= 80) {
            strengths.push('Excellent variety of quality targets available');
        } else if (scores.objectCount < 40) {
            weaknesses.push('Limited number of quality objects');
            recommendations.push('Consider expanding search radius or adjusting magnitude limits');
        }

        // Moon interference analysis
        if (scores.moonInterference >= 80) {
            strengths.push('Minimal moon interference throughout the session');
        } else if (scores.moonInterference < 50) {
            weaknesses.push('Significant moon interference reduces imaging time');
            recommendations.push('Consider scheduling session during new moon phase');
        }

        // Distribution analysis
        if (scores.distribution >= 70) {
            strengths.push('Objects well-distributed throughout the night');
        } else if (scores.distribution < 50) {
            weaknesses.push('Objects concentrated in limited time windows');
            recommendations.push('Plan equipment changes during gaps in object visibility');
        }

        // Altitude analysis
        if (scores.altitude >= 75) {
            strengths.push('Excellent average altitude reduces atmospheric interference');
        } else if (scores.altitude < 50) {
            weaknesses.push('Lower altitudes may affect image quality');
            recommendations.push('Focus on objects at peak altitude for best results');
        }

        // Diversity analysis
        if (scores.diversity >= 70) {
            strengths.push('Good diversity of object types for varied imaging');
        } else if (scores.diversity < 40) {
            weaknesses.push('Limited object type diversity');
            recommendations.push('Explore different sky directions for more variety');
        }

        return {
            strengths: strengths,
            weaknesses: weaknesses,
            recommendations: recommendations
        };
    }

    /**
     * Generate analysis of optimal windows
     * @param {Array} windows - Optimal imaging windows
     * @param {Array} objects - All objects
     * @returns {string} Window analysis
     */
    generateWindowAnalysis(windows, objects) {
        if (windows.length === 0) {
            return 'No optimal imaging windows identified. Consider adjusting search parameters.';
        }

        let analysis = `Identified ${windows.length} optimal imaging window(s). `;

        if (windows.length > 1) {
            const scoreDiff = windows[0].score - windows[windows.length - 1].score;
            if (scoreDiff < 20) {
                analysis += 'Windows are of similar quality, providing flexibility for session planning.';
            } else {
                analysis += 'Focus on the highest-rated window for best results.';
            }
        }

        return analysis;
    }

    /**
     * Format time for display
     * @param {Date} date - Date to format
     * @returns {string} Formatted time
     */
    formatTime(date) {
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    }
}

export default SessionAnalytics;
